import { useState, useEffect } from "react";
import { useLocation } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { toast } from "../../../../../components/toastNotification";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "components/TableFilters";
import {
  addStateOfficeBMO,
  editStateOfficeBMO,
  rowEditOfficeBMO,
  officeBMOFetching,
  officeBMOFilterText,
  officeBMOReFetching,
  selectedRowsStateOfficeBMO,
} from "../atoms/atoms";
import OfficeBmoForm from "../form/OfficeBMOForm";
import useDeactivateOfficeBMO from "../hooks/useDeactivateOfficeBMO";
import useGetOfficeBMO from "../hooks/usetGetOfficeBMO";
import { OfficeBMOType, OfficeBMOTypeTableData } from "../types";

import Timer from "components/Timer";

export const OfficeBMOTable = () => {
  const { data: directData, refetch: reFetch } = useGetOfficeBMO();

  const BulkDelete = useDeactivateOfficeBMO();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(addStateOfficeBMO);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateOfficeBMO
  );
  const [actionEditState, setActionEditState] =
    useRecoilState(editStateOfficeBMO);
  const [filterTextState, setFilterTextState] =
    useRecoilState(officeBMOFilterText);
  const setEditRowData = useSetRecoilState(rowEditOfficeBMO);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(officeBMOFetching);
  const isReFetching = useRecoilValue(officeBMOReFetching);

  //Local States
  const [tableData, setTableData] = useState<OfficeBMOType[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<OfficeBMOType[]>(
    []
  );

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officebmo_add;
  const editPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officebmo_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officebmo_delete;

  // Get Data
  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status == 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  //For filtered data update

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: OfficeBMOType[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].bmoName);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  // For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.bmoName &&
        item.bmoName
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );

  useEffect(() => {
    if (!recent) setTableFilteredData(filtereditems);
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: OfficeBMOTypeTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });

    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: OfficeBMOTypeTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);

    const ids = selectedRows && selectedRows.map((row) => row.bmoId);

    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Office BMOs" : "Office BMO"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Office BMO",
      field: "bmoName",
      customSort: (a, b) =>
        a.bmoName && b.bmoName
          ? a.bmoName.trim().localeCompare(b.bmoName.trim())
          : a.bmoName
          ? 0
          : -1,
    },

    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },

    {
      title: "Modified By",
      field: "updatedBy",
      customSort: (a, b) =>
        a.updatedBy && b.updatedBy
          ? a.updatedBy.trim().localeCompare(b.updatedBy.trim())
          : a.updatedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "updatedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center mr-5">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="bg-white p-4">
        {actionAddState || actionEditState ? (
          <OfficeBmoForm setRecent={setRecent} />
        ) : (
          <>
            <div className="flex flex-row justify-between items-center">
              <TableFilters
                defaultText={filterTextState}
                recentText={recent}
                setRecent={setRecent}
                textInputName={"bmoName"}
                columnDetails={"Office BMO"}
                filterText={setFilterTextState}
                disabledButton={
                  actionEditState ||
                  (selectedRows !== undefined && selectedRows.length > 0)
                }
                disabledTextBox={
                  selectedRows !== undefined && selectedRows.length > 0
                }
              />

              <div className="">
                {userPermissions.includes(addPermission) ? (
                  <AddButton
                    buttonText="New Office BMO"
                    onClick={() => handleAdd()}
                    disabled={
                      actionEditState ||
                      (selectedRows !== undefined && selectedRows.length > 0)
                    }
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>
            <div>
              <MaterialTableTS
                data={
                  isFetching
                    ? []
                    : filterTextState === "" && recent === ""
                    ? tableData
                    : filterTextState === ""
                    ? tableFilteredData
                    : filtereditems
                }
                showTitle={true}
                columns={matColumns}
                isLoading={isFetching}
                onSelectionChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                selection={
                  !actionEditState &&
                  !actionAddState &&
                  userPermissions.includes(deletePermission)
                }
                setConfirmModalOpen={setOpenModalDelete}
              />
            </div>

            <ConfirmBoxDelete
              ModalTitle="Deactivate Office BMO"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
                setSelectedRows([]);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
      </div>
    </>
  );
};

export default OfficeBMOTable;
